/**
 * Browser-local identity primitive. Support execution binds the opaque receipt
 * to the correlated SDK result and reviews the record before permitting writes.
 *
 * Application policy is supplied as trusted profiles, outside the SDK. No ID
 * is accepted from a model value, URL argument, display index, or saved receipt.
 */

/** Authored application configuration, never model-supplied capture data. */
export type RecordIdentityProfile = {
  id: string;
  origin: string;
  sourcePath: string;
} & ({
  idAttribute: string;
  /** Additional ID formats require their own validation; arbitrary strings fail. */
  idFormat: 'uuid';
  /** Same-origin path with exactly one complete {recordId} path segment. */
  destinationPathTemplate: string;
} | { idFormat: 'visible-labels' });

export interface RecordIdentityOwner {
  runId: string;
  planId: string;
  planVersion: number;
}

export interface RecordIdentityLabel {
  selector: string;
  text: string;
}

export interface RecordIdentityCapture {
  key: string;
  profile: string;
  rowSelector: string;
  name: RecordIdentityLabel;
  discriminator: RecordIdentityLabel;
}

export interface RecordIdentityReceipt {
  key: string;
  profile: string;
  token: string;
}

export interface ResolvedRecordIdentity {
  /** Browser-local URL: never include it in model input or the capture ack. */
  destination: string;
  /** Retains authority across awaits; false after replacement or recapture. */
  isCurrent: () => boolean;
  /** Must guard every read, fill and click at its immediate action boundary. */
  matchesCurrentPage: () => boolean;
  scopeSelector?: string;
}

type Entry = {
  owner: RecordIdentityOwner;
  receipt: Readonly<RecordIdentityReceipt>;
  profile: Readonly<RecordIdentityProfile>;
  destination: string;
  capture: RecordIdentityCapture;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const KEY = /^[a-z][a-zA-Z0-9_]{0,63}$/;
const PROFILE_ID = /^[a-z][a-z0-9-]{0,63}$/;
const ATTRIBUTE = /^[a-z][a-z0-9-]{0,63}$/;
const RECORD_ID_SEGMENT = '{recordId}';
const normalize = (value: string) => value.replace(/\s+/g, ' ').trim();
const fail = (): never => { throw new Error('The intended record identity is not verified.'); };
function visible(element: Element): element is HTMLElement {
  if (!(element instanceof HTMLElement) || !element.isConnected ||
      ![...element.getClientRects()].some(rect => rect.width > 0 && rect.height > 0) ||
      element.closest('[hidden], [inert], [aria-hidden="true"], [data-floe-widget]')) return false;
  for (let ancestor: Element | null = element; ancestor; ancestor = ancestor.parentElement) {
    const style = getComputedStyle(ancestor);
    if (style.visibility === 'hidden' || style.visibility === 'collapse' || style.display === 'none' ||
        style.contentVisibility === 'hidden' || Number.parseFloat(style.opacity) === 0) return false;
  }
  return true;
}

function objectKeys(value: unknown, keys: string[]): boolean {
  return !!value && typeof value === 'object' && !Array.isArray(value) &&
    Object.keys(value).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
}

function validPath(path: unknown, origin: string): path is string {
  if (typeof path !== 'string' || path.length > 1000 || !path.startsWith('/') ||
      /[\s\\?#%{}]/.test(path)) return false;
  const url = new URL(path, origin);
  return url.origin === origin && url.href === origin + path;
}

function validProfile(profile: RecordIdentityProfile): boolean {
  const labelOnly = profile?.idFormat === 'visible-labels';
  if (!objectKeys(profile, labelOnly ? ['id', 'origin', 'sourcePath', 'idFormat'] :
      ['id', 'origin', 'sourcePath', 'idAttribute', 'idFormat', 'destinationPathTemplate']) ||
      typeof profile.id !== 'string' || !PROFILE_ID.test(profile.id) ||
      typeof profile.origin !== 'string') return false;
  if (!labelOnly && (typeof profile.idAttribute !== 'string' ||
      !ATTRIBUTE.test(profile.idAttribute) || profile.idFormat !== 'uuid' ||
      typeof profile.destinationPathTemplate !== 'string')) return false;
  try {
    const origin = new URL(profile.origin);
    if (origin.protocol !== 'https:' || origin.origin !== profile.origin || !validPath(profile.sourcePath, profile.origin)) return false;
    if (labelOnly) return true;
    const segments = profile.destinationPathTemplate.split('/');
    return origin.protocol === 'https:' && origin.origin === profile.origin &&
      validPath(profile.sourcePath, profile.origin) &&
      segments.filter(segment => segment === RECORD_ID_SEGMENT).length === 1 &&
      validPath(profile.destinationPathTemplate.replace(RECORD_ID_SEGMENT, 'record'), profile.origin);
  } catch { return false; }
}

function validSelector(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim() || value.length > 500 || value.includes('{{')) return false;
  try { document.querySelectorAll(value); return true; } catch { return false; }
}

function validLabel(value: RecordIdentityLabel): boolean {
  return objectKeys(value, ['selector', 'text']) && validSelector(value.selector) &&
    typeof value.text === 'string' && value.text.length <= 500 && !!normalize(value.text) &&
    !value.text.includes('{{');
}

function validOwner(owner: RecordIdentityOwner): boolean {
  return !!owner && typeof owner.runId === 'string' && !!owner.runId &&
    typeof owner.planId === 'string' && !!owner.planId &&
    Number.isInteger(owner.planVersion) && owner.planVersion > 0;
}

function sameOwner(left: RecordIdentityOwner, right: RecordIdentityOwner): boolean {
  return validOwner(right) && left.runId === right.runId && left.planId === right.planId &&
    left.planVersion === right.planVersion;
}

function exactLabel(row: HTMLElement, label: RecordIdentityLabel, ownerSelector: string): HTMLElement | null {
  const labels = [...row.querySelectorAll(label.selector)].filter(visible);
  return labels.length === 1 && labels[0].closest(ownerSelector) === row &&
    typeof labels[0].innerText === 'string' &&
    normalize(labels[0].innerText) === normalize(label.text) ? labels[0] : null;
}

export class SupportRecordIdentities {
  private readonly profiles = new Map<string, Readonly<RecordIdentityProfile>>();
  private activeRun: string | null = null;
  private retiredRuns = new Set<string>();
  private entries = new Map<string, Entry>();

  /** No built-in tenant profiles. Snapshot trusted configuration before a run. */
  constructor(profiles: readonly RecordIdentityProfile[] = []) {
    if (!Array.isArray(profiles) || profiles.length > 16) throw new Error('Invalid record identity profiles.');
    for (const profile of profiles) {
      if (!validProfile(profile) || this.profiles.has(profile.id)) {
        throw new Error('Invalid or duplicate record identity profile.');
      }
      this.profiles.set(profile.id, Object.freeze({...profile}));
    }
  }

  activate(runId: string): boolean {
    if (typeof runId !== 'string' || !runId || this.retiredRuns.has(runId)) return false;
    if (this.activeRun !== runId) {
      this.retire();
      this.activeRun = runId;
    }
    return true;
  }

  retire(): void {
    if (this.activeRun) this.retiredRuns.add(this.activeRun);
    this.activeRun = null;
    this.entries.clear();
  }

  /** A failed or repeated observation retires every earlier token for its key. */
  capture(owner: RecordIdentityOwner, capture: RecordIdentityCapture): Readonly<RecordIdentityReceipt> {
    if (!validOwner(owner) || owner.runId !== this.activeRun) return fail();
    // Reject stale runs before invalidating the replacement's record.
    if (capture && typeof capture.key === 'string') this.entries.delete(capture.key);
    if (!objectKeys(capture, ['key', 'profile', 'rowSelector', 'name', 'discriminator']) ||
        typeof capture.key !== 'string' || !KEY.test(capture.key) ||
        typeof capture.profile !== 'string' || !validSelector(capture.rowSelector) ||
        !validLabel(capture.name) || !validLabel(capture.discriminator) ||
        capture.name.selector === capture.discriminator.selector ||
        normalize(capture.name.text) === normalize(capture.discriminator.text) ||
        this.entries.size >= 16) return fail();
    const profile = this.profiles.get(capture.profile);
    if (!profile || location.href !== profile.origin + profile.sourcePath) return fail();
    try {
      const rows = [...document.querySelectorAll(capture.rowSelector)].filter(visible);
      const candidates = rows.filter(row => {
        const ownerSelector = profile.idFormat === 'uuid' ? `[${profile.idAttribute}]` : capture.rowSelector;
        const name = exactLabel(row, capture.name, ownerSelector);
        const discriminator = exactLabel(row, capture.discriminator, ownerSelector);
        return name && discriminator && name !== discriminator &&
          !name.contains(discriminator) && !discriminator.contains(name);
      });
      if (candidates.length !== 1) return fail();
      let destination = profile.origin + profile.sourcePath;
      if (profile.idFormat === 'uuid') {
        const uuid = candidates[0].getAttribute(profile.idAttribute);
        if (!uuid || !UUID.test(uuid) || rows.filter(row => row.getAttribute(profile.idAttribute) === uuid).length !== 1) return fail();
        destination = profile.origin + profile.destinationPathTemplate.replace(RECORD_ID_SEGMENT, uuid);
      }
      // The actual UUID stays in this browser instance. Only the opaque token
      // is returned for later correlated actions from this exact plan/run.
      const receipt = Object.freeze({key: capture.key, profile: profile.id, token: crypto.randomUUID()});
      this.entries.set(capture.key, {
        owner: {...owner}, receipt, profile,
        destination, capture: JSON.parse(JSON.stringify(capture)),
      });
      return receipt;
    } catch { return fail(); }
  }

  resolve(owner: RecordIdentityOwner, binding: RecordIdentityReceipt): ResolvedRecordIdentity {
    if (!objectKeys(binding, ['key', 'profile', 'token'])) return fail();
    const entry = this.entries.get(binding.key);
    const isCurrent = () => !!entry && this.entries.get(binding.key) === entry &&
      this.activeRun === entry.owner.runId && sameOwner(entry.owner, owner) &&
      entry.receipt.token === binding.token && entry.receipt.profile === binding.profile &&
      location.origin === entry.profile.origin;
    if (!entry || !isCurrent()) return fail();
    const destination = entry.destination;
    const scopeSelector = entry.profile.idFormat === 'visible-labels' ? entry.capture.rowSelector : undefined;
    const labelsCurrent = () => {
      if (!scopeSelector) return true;
      try {
        const rows = [...document.querySelectorAll(scopeSelector)].filter(visible).filter(row => {
          const name = exactLabel(row, entry.capture.name, scopeSelector);
          const discriminator = exactLabel(row, entry.capture.discriminator, scopeSelector);
          return name && discriminator && name !== discriminator &&
            !name.contains(discriminator) && !discriminator.contains(name);
        });
        return rows.length === 1;
      } catch { return false; }
    };
    return Object.freeze({
      destination, isCurrent, ...(scopeSelector ? {scopeSelector} : {}),
      matchesCurrentPage: () => isCurrent() && location.href === destination && labelsCurrent(),
    });
  }
}
